import React, { useEffect, useState } from 'react';
import {
  useGetIdentity,
  useGetOne,
  useNotify,
  useRefresh,
} from 'react-admin';
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { getToken } from '../authentication/authProvider';
import {
  CoronerAddress,
  coronerRowAddress,
  extractUnit,
  formatMailingBlock,
  parseCoronerAddress,
} from '../coroners/coronerAddress';

const NEST_API_URL =
  process.env.REACT_APP_PAPERMAN_API_URL || 'http://localhost:3001/';

export const DEFAULT_AMOUNT_CENTS = 7500;

export interface FuneralCoronerRow {
  id: number;
  funeral_id: number;
  coroner_id: string;
  coroner_name_snapshot: string;
  coroner_address_snapshot: string | null;
  coroner_address_line1: string | null;
  coroner_address_line2: string | null;
  coroner_city: string | null;
  coroner_province: string | null;
  coroner_postal_code: string | null;
  payment_status: 'paid' | 'pending' | 'failed' | null;
  payment_amount_cents: number | null;
}

interface AddressForm {
  addressLine1: string;
  addressLine2: string;
  city: string;
  province: string;
  postalCode: string;
}

export function CoronerPaymentModal({
  open,
  onClose,
  row,
}: {
  open: boolean;
  onClose: () => void;
  row: FuneralCoronerRow;
}) {
  const notify = useNotify();
  const refresh = useRefresh();
  const { identity } = useGetIdentity();

  const [amountDollars, setAmountDollars] = useState<string>(
    String((row.payment_amount_cents ?? DEFAULT_AMOUNT_CENTS) / 100),
  );
  const [addr, setAddr] = useState<AddressForm>(() => initialAddress(row, null));
  const [sending, setSending] = useState(false);

  // The coroner's current record — newer than this funeral's snapshot if the
  // address (e.g. a unit) was fixed on the Coroners page after assignment.
  const { data: coroner, isLoading: coronerLoading } = useGetOne<any>(
    'coroners',
    { id: row.coroner_id },
    { enabled: open && !!row.coroner_id },
  );

  const { data: funeral } = useGetOne<any>('funerals', { id: row.funeral_id });
  const deceasedClientId = funeral?.deceased_client_id;
  const { data: client } = useGetOne<any>(
    'clients',
    { id: deceasedClientId },
    { enabled: !!deceasedClientId },
  );

  useEffect(() => {
    if (!open || coronerLoading) return;
    setAmountDollars(
      String((row.payment_amount_cents ?? DEFAULT_AMOUNT_CENTS) / 100),
    );
    setAddr(initialAddress(row, coroner ?? null));
  }, [open, row.id, coronerLoading, coroner?.id]);

  const deceasedName = client
    ? `${client.last_name ?? ''}, ${client.first_name ?? ''}`.replace(/^,\s*|,\s*$/g, '')
    : '…';
  const memo = client
    ? `Re: ${client.last_name ?? ''}, ${client.first_name ?? ''}`
    : 'Re: …';

  const addressValid =
    addr.addressLine1.trim() !== '' &&
    addr.city.trim() !== '' &&
    addr.province.trim() !== '' &&
    addr.postalCode.trim() !== '';

  const handleSend = async () => {
    const cents = Math.round(Number(amountDollars) * 100);
    if (!Number.isFinite(cents) || cents <= 0) {
      notify('Enter a valid amount', { type: 'warning' });
      return;
    }
    if (!addressValid) {
      notify('Fill in address line 1, city, province, and postal code', {
        type: 'warning',
      });
      return;
    }
    setSending(true);
    try {
      const token = await getToken();
      const res = await fetch(`${NEST_API_URL}coroners/payments/${row.id}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          amountCents: cents,
          assignedByUserId: numericId((identity as any)?.id),
          assignedByUserName: (identity as any)?.fullName ?? (identity as any)?.email ?? null,
          coronerAddress: {
            addressLine1: addr.addressLine1.trim(),
            addressLine2: addr.addressLine2.trim() || undefined,
            city: addr.city.trim(),
            provinceOrState: addr.province.trim(),
            postalOrZip: addr.postalCode.trim(),
          },
        }),
      });
      const body = await res.json().catch(() => ({}));
      if (!res.ok) {
        throw new Error(body?.message || `HTTP ${res.status}`);
      }
      if (body?.paymentStatus === 'paid') {
        notify(`Cheque sent for ${row.coroner_name_snapshot}`, { type: 'success' });
      } else {
        notify(`Payment failed: ${body?.error || 'unknown'}`, { type: 'error' });
      }
      refresh();
      onClose();
    } catch (err: any) {
      notify(`Could not send cheque: ${err?.message ?? 'unknown'}`, { type: 'error' });
    } finally {
      setSending(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={() => !sending && onClose()}
      maxWidth="sm"
      fullWidth
      onClick={(e) => e.stopPropagation()}
    >
      <DialogTitle>Pay coroner — {row.coroner_name_snapshot}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ my: 1 }}>
          <Box>
            <Typography variant="caption" color="text.secondary">For</Typography>
            <Typography variant="body1">{deceasedName}</Typography>
          </Box>
          <TextField
            label="Amount"
            type="number"
            size="small"
            value={amountDollars}
            onChange={(e) => setAmountDollars(e.target.value)}
            InputProps={{ startAdornment: <Box sx={{ pr: 0.5 }}>$</Box> }}
            inputProps={{ step: '0.01', min: '0' }}
            fullWidth
          />
          <ReadOnlyRow label="Memo" value={memo} />
          <ReadOnlyRow
            label="From"
            value="Cleo Funeral Group Inc., 100 University Ave, 5th Floor, Toronto ON M5J 1V6"
          />
          <Box>
            <Typography variant="caption" color="text.secondary">To</Typography>
            <Typography variant="body2" sx={{ mb: 1 }}>
              Dr. {row.coroner_name_snapshot}
            </Typography>
            <Stack spacing={1.5}>
              <TextField
                label="Street address"
                size="small"
                value={addr.addressLine1}
                onChange={(e) => setAddr((a) => ({ ...a, addressLine1: e.target.value }))}
                required
                fullWidth
              />
              <TextField
                label="Unit / Apt / Suite"
                size="small"
                value={addr.addressLine2}
                onChange={(e) => setAddr((a) => ({ ...a, addressLine2: e.target.value }))}
                placeholder="e.g. Unit 4, Suite 200, App. 5"
                fullWidth
              />
              <Stack direction="row" spacing={1.5}>
                <TextField
                  label="City"
                  size="small"
                  value={addr.city}
                  onChange={(e) => setAddr((a) => ({ ...a, city: e.target.value }))}
                  required
                  fullWidth
                />
                <TextField
                  label="Province"
                  size="small"
                  value={addr.province}
                  onChange={(e) => setAddr((a) => ({ ...a, province: e.target.value }))}
                  required
                  sx={{ width: 110 }}
                />
                <TextField
                  label="Postal code"
                  size="small"
                  value={addr.postalCode}
                  onChange={(e) => setAddr((a) => ({ ...a, postalCode: e.target.value }))}
                  required
                  sx={{ width: 140 }}
                />
              </Stack>
            </Stack>
            <MailingPreview
              lines={formatMailingBlock(`Dr. ${row.coroner_name_snapshot}`, toCoronerAddress(addr))}
            />
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ mt: 0.5, display: 'block' }}
            >
              Edits are saved back to the coroner for next time.
            </Typography>
          </Box>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={sending}>Cancel</Button>
        <Button
          variant="contained"
          onClick={() => void handleSend()}
          disabled={sending || !addressValid}
        >
          {sending ? <CircularProgress size={20} /> : 'Send cheque'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

function numericId(v: unknown): number | null {
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
}

// Seed the editable address form from the coroner's current record, else this
// funeral's snapshot (structured columns, then the parsed legacy line). A unit
// folded into the street line ("503-18 Lower Village Gate") is split out so it
// shows in the Unit field.
function initialAddress(row: FuneralCoronerRow, coroner: any | null): AddressForm {
  const found: CoronerAddress | null =
    (coroner && coronerRowAddress(coroner)) ||
    (row.coroner_address_line1
      ? {
          addressLine1: row.coroner_address_line1,
          addressLine2: row.coroner_address_line2,
          city: row.coroner_city ?? '',
          provinceOrState: row.coroner_province ?? '',
          postalOrZip: row.coroner_postal_code ?? '',
        }
      : parseCoronerAddress(row.coroner_address_snapshot));
  if (!found) {
    return { addressLine1: '', addressLine2: '', city: '', province: '', postalCode: '' };
  }
  let line1 = found.addressLine1;
  let line2 = found.addressLine2 ?? '';
  if (!line2.trim()) {
    const split = extractUnit(line1);
    if (split.unit) {
      line1 = split.street;
      line2 = split.unit;
    }
  }
  return {
    addressLine1: line1,
    addressLine2: line2,
    city: found.city,
    province: found.provinceOrState,
    postalCode: found.postalOrZip,
  };
}

function toCoronerAddress(a: AddressForm): CoronerAddress {
  return {
    addressLine1: a.addressLine1,
    addressLine2: a.addressLine2 || null,
    city: a.city,
    provinceOrState: a.province,
    postalOrZip: a.postalCode,
  };
}

function MailingPreview({ lines }: { lines: string[] }) {
  return (
    <Box sx={{ mt: 1.5 }}>
      <Typography variant="caption" color="text.secondary">
        Printed on the cheque
      </Typography>
      <Box
        sx={{
          border: 1,
          borderColor: 'divider',
          borderRadius: 1,
          px: 1.5,
          py: 1,
          fontFamily: 'monospace',
          fontSize: 13,
        }}
      >
        {lines.map((l, i) => (
          <div key={i}>{l}</div>
        ))}
      </Box>
    </Box>
  );
}

function ReadOnlyRow({ label, value }: { label: string; value: string }) {
  return (
    <Box>
      <Typography variant="caption" color="text.secondary">{label}</Typography>
      <Typography variant="body2">{value}</Typography>
    </Box>
  );
}
